import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LINKS, BRAND } from "@/lib/content";
import { useT } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useT();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const handleAnchor = (e, to) => {
        e.preventDefault();
        const hash = to.split("#")[1];
        if (location.pathname !== "/") {
            navigate(`/#${hash}`);
        } else {
            const el = document.getElementById(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const NAV = [
        { label: t.nav.home, to: "/", type: "route" },
        { label: t.nav.services, to: "/services", type: "route" },
        { label: t.nav.about, to: "/#rolunk", type: "anchor" },
        { label: t.nav.references, to: "/#referenciak", type: "anchor" },
        { label: t.nav.contact, to: "/#kapcsolat", type: "anchor" },
        { label: t.nav.faq, to: "/faq", type: "route" },
    ];

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
                scrolled || open
                    ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="container-x flex items-center justify-between h-[72px] md:h-[80px]">
                <Link
                    to="/"
                    className="flex items-center gap-3 group"
                    data-testid="header-logo-link"
                    aria-label="SalesShark"
                >
                    <img
                        src={BRAND.logo}
                        alt="SalesShark"
                        className="h-11 w-11 md:h-12 md:w-12 object-contain"
                        loading="eager"
                    />
                    <span className="font-display text-xl md:text-2xl font-semibold tracking-tight text-white">
                        SalesShark
                    </span>
                </Link>

                <nav
                    className="hidden lg:flex items-center gap-1"
                    aria-label={t.nav.home}
                >
                    {NAV.map((item) => {
                        if (item.type === "route") {
                            return (
                                <NavLink
                                    key={item.label}
                                    to={item.to}
                                    end={item.to === "/"}
                                    className={({ isActive }) =>
                                        `px-3 py-2 text-[0.9rem] font-medium transition-colors ${
                                            isActive
                                                ? "text-white"
                                                : "text-white/70 hover:text-white"
                                        }`
                                    }
                                    data-testid={`nav-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </NavLink>
                            );
                        }
                        return (
                            <a
                                key={item.label}
                                href={item.to}
                                onClick={(e) => handleAnchor(e, item.to)}
                                className="px-3 py-2 text-[0.9rem] font-medium text-white/70 hover:text-white transition-colors"
                                data-testid={`nav-${item.label.toLowerCase()}`}
                            >
                                {item.label}
                            </a>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-2">
                    <LanguageSwitcher />
                    <a
                        href={LINKS.consultation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary hidden md:inline-flex text-sm py-2.5 px-4"
                        data-testid="header-cta-consultation"
                    >
                        {t.nav.consultation}
                    </a>
                    <button
                        type="button"
                        className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/10 text-white"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={t.nav.home}
                        aria-expanded={open}
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu drawer */}
            <div
                className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
                    open
                        ? "max-h-[80vh] opacity-100"
                        : "max-h-0 opacity-0 pointer-events-none"
                }`}
                data-testid="mobile-menu"
            >
                <div className="container-x pb-8 pt-2 flex flex-col gap-1 border-t border-white/10">
                    {NAV.map((item) => {
                        if (item.type === "route") {
                            return (
                                <NavLink
                                    key={item.label}
                                    to={item.to}
                                    end={item.to === "/"}
                                    className={({ isActive }) =>
                                        `py-4 text-[1.05rem] font-medium border-b border-white/5 ${
                                            isActive
                                                ? "text-[color:var(--accent)]"
                                                : "text-white/85"
                                        }`
                                    }
                                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </NavLink>
                            );
                        }
                        return (
                            <a
                                key={item.label}
                                href={item.to}
                                onClick={(e) => handleAnchor(e, item.to)}
                                className="py-4 text-[1.05rem] font-medium text-white/85 border-b border-white/5"
                                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                            >
                                {item.label}
                            </a>
                        );
                    })}
                    <a
                        href={LINKS.consultation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary mt-5 w-full"
                        data-testid="mobile-cta-consultation"
                    >
                        {t.nav.consultation}
                    </a>
                </div>
            </div>
        </header>
    );
}
